import { useCallback, useEffect, useRef, useState } from 'react'
import { apiFetch } from '@/lib/apiFetch'
import { useExerciseSession } from '@/hooks/useExerciseSession'
import {
  ActCatalog,
  ActFocusKey,
  ActQuestionLog,
  ActSummary,
  ACT_EXERCISE_TYPE,
  CatalogFocus,
  CatalogPaceLevel,
  CatalogSection,
  FOCUS_PACE,
  MODE_PRACTICE,
  MODE_TEST,
  SectionPace,
  StrategyStat,
  fetchCatalog,
  focusHit,
  paceBudget,
  paceLevelOf,
  sectionBudget,
  sectionOf,
} from '@/lib/actModes'

/** Pick a section, pick what you're working on, then run questions. */
export type ActStage = 'section' | 'mode' | 'run' | 'done'

/** Where a single question is: committing, solving, or being graded. */
export type ActPhase = 'commit' | 'working' | 'grade'

export interface CommitInput {
  predictedSeconds?: number | null
  predictedCorrect?: boolean | null
  plannedStrategies?: string[]
}

/**
 * Drives ACT practice end to end. Two shapes, one loop: loose practice asks him
 * to call one thing before each question, and a full-section run drops the
 * calling and holds him to a pace budget instead. Either way it's commit,
 * hidden clock, right or wrong, straight into the next question -- nothing to
 * fill in afterward. Wraps the shared exercise session, so ACT practice feeds
 * the same duration tracking, goals, and streak machinery as every exercise.
 */
export function useActFlow() {
  const session = useExerciseSession({ exerciseType: ACT_EXERCISE_TYPE })

  const [catalog, setCatalog] = useState<ActCatalog | null>(null)
  const [pace, setPace] = useState<SectionPace[]>([])

  const [stage, setStage] = useState<ActStage>('section')
  const [mode, setMode] = useState<string>(MODE_PRACTICE)
  const [section, setSection] = useState<string | null>(null)
  const [focusKey, setFocusKey] = useState<ActFocusKey | null>(null)
  const [paceLevelKey, setPaceLevelKey] = useState<string | null>(null)

  const [phase, setPhase] = useState<ActPhase>('commit')
  const [currentLog, setCurrentLog] = useState<ActQuestionLog | null>(null)
  const [startedAt, setStartedAt] = useState<number | null>(null)
  const [elapsedSeconds, setElapsedSeconds] = useState<number | null>(null)

  const [logs, setLogs] = useState<ActQuestionLog[]>([])
  const [last, setLast] = useState<ActQuestionLog | null>(null)
  const [summary, setSummary] = useState<ActSummary | null>(null)
  const [strategyStats, setStrategyStats] = useState<StrategyStat[]>([])

  const [runStartedAt, setRunStartedAt] = useState<number | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const startedRef = useRef(false)

  useEffect(() => {
    fetchCatalog().then(setCatalog).catch(() => {})
    // Recent pace per section, so a full run opens on the rung he's earned
    // rather than at the wall. Absent history just means the default rung.
    apiFetch<{ pace: SectionPace[] }>('/act/insights?days=60')
      .then(data => setPace(data.pace || []))
      .catch(() => {})
  }, [])

  const begin = useCallback(async () => {
    if (startedRef.current) return
    startedRef.current = true
    try {
      await session.startSession()
    } catch {
      startedRef.current = false
      setError('Could not start a session. Check your connection and try again.')
    }
  }, [session])

  const sectionSpec: CatalogSection | null = sectionOf(catalog, section)
  const focus: CatalogFocus | null = catalog && focusKey
    ? catalog.focuses.find(f => f.key === focusKey) || null
    : null
  const paceLevel: CatalogPaceLevel | null = paceLevelOf(catalog, paceLevelKey)
  const sectionPace = pace.find(p => p.section === section) || null

  /** Seconds a single question gets in the current full-section run. */
  const questionBudget = paceBudget(sectionSpec, paceLevel)
  const runBudgetSeconds = sectionBudget(sectionSpec, paceLevel)
  const questionsPlanned = sectionSpec?.questions ?? 0

  const chooseSection = useCallback((key: string) => {
    setSection(key)
    setStage('mode')
  }, [])

  /** Opens on the tightest rung his recent average already clears. */
  const recommendedPaceLevel = useCallback((sectionKey: string | null): string => {
    const row = pace.find(p => p.section === sectionKey)
    return row?.recommendedPaceLevel || catalog?.paceLevels?.[1]?.key || 'BUILD'
  }, [pace, catalog])

  const startPractice = useCallback((key: ActFocusKey) => {
    setFocusKey(key)
    setMode(MODE_PRACTICE)
    setPaceLevelKey(null)
    setPhase('commit')
    setLast(null)
    setStage('run')
  }, [])

  const startTest = useCallback((levelKey?: string) => {
    setFocusKey(FOCUS_PACE)
    setMode(MODE_TEST)
    setPaceLevelKey(levelKey || recommendedPaceLevel(section))
    setLast(null)
    setRunStartedAt(null)
    setStage('run')
  }, [recommendedPaceLevel, section])

  /** Creates the row for a question and starts its clock. */
  const openQuestion = useCallback(async (input: CommitInput, budgetSeconds: number | null) => {
    if (!session.sessionId) {
      setError('No active session yet — give it a second.')
      return false
    }
    if (!focusKey) return false
    setBusy(true)
    setError(null)
    try {
      const now = Date.now()
      const log = await apiFetch<ActQuestionLog>('/act/logs', {
        method: 'POST',
        body: JSON.stringify({
          sessionId: session.sessionId,
          focus: focusKey,
          mode,
          section,
          predictedSeconds: input.predictedSeconds ?? null,
          targetSeconds: budgetSeconds,
          predictedCorrect: input.predictedCorrect ?? null,
          plannedStrategies: input.plannedStrategies ?? [],
          startedAt: now,
        }),
      })
      setCurrentLog(log)
      setStartedAt(now)
      setElapsedSeconds(null)
      setPhase('working')
      return true
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not start that question.')
      return false
    } finally {
      setBusy(false)
    }
  }, [focusKey, mode, section, session.sessionId])

  /** Practice: he's committed to something, start the clock. */
  const startQuestion = useCallback((input: CommitInput) => openQuestion(input, null), [openQuestion])

  /** He hit "done" -- freeze the clock before asking anything else. */
  const stopTimer = useCallback(() => {
    if (startedAt == null) return
    setElapsedSeconds(Math.max(1, Math.round((Date.now() - startedAt) / 1000)))
    setPhase('grade')
  }, [startedAt])

  const finish = useCallback(async () => {
    const sessionId = session.sessionId
    setStage('done')
    setPhase('commit')
    setCurrentLog(null)
    setStartedAt(null)
    try {
      await session.endSession()
    } catch {}
    if (!sessionId) return
    try {
      const review = await apiFetch<{ logs: ActQuestionLog[]; summary: ActSummary; strategies: StrategyStat[] }>(
        `/act/sessions/${sessionId}/review`
      )
      setLogs(review.logs || [])
      setSummary(review.summary || null)
      setStrategyStats(review.strategies || [])
    } catch {}
  }, [session])

  /**
   * Right or wrong, and straight on. In a full-section run the next question's
   * clock starts on the same tap -- stopping to admire a stat is exactly the
   * habit a timed section punishes.
   */
  const grade = useCallback(async (correct: boolean) => {
    if (!currentLog) return
    const seconds = mode === MODE_TEST && startedAt != null
      ? Math.max(1, Math.round((Date.now() - startedAt) / 1000))
      : elapsedSeconds
    if (seconds == null) return

    setBusy(true)
    setError(null)
    try {
      const attempt = await session.recordAttempt(null, correct, seconds * 1000, {
        actLogId: currentLog.id,
        focus: currentLog.focus,
        mode,
        section: currentLog.section,
        predictedSeconds: currentLog.predictedSeconds,
        targetSeconds: currentLog.targetSeconds,
        predictedCorrect: currentLog.predictedCorrect,
      })
      const updated = await apiFetch<ActQuestionLog>(`/act/logs/${currentLog.id}/complete`, {
        method: 'POST',
        body: JSON.stringify({
          actualSeconds: seconds,
          actualCorrect: correct,
          attemptId: attempt && typeof attempt.id === 'number' ? attempt.id : null,
        }),
      })
      setLogs(prev => [...prev, updated])
      setLast(updated)
      setCurrentLog(null)
      setStartedAt(null)
      setElapsedSeconds(null)
      setPhase('commit')
      return updated
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save that result.')
      return null
    } finally {
      setBusy(false)
    }
  }, [currentLog, elapsedSeconds, mode, session, startedAt])

  /**
   * Opens the next question of a full-section run on its own budget. The first
   * call also starts the section clock, so the run begins when he does.
   */
  const nextTestQuestion = useCallback(async () => {
    setRunStartedAt(prev => prev ?? Date.now())
    return openQuestion({}, questionBudget)
  }, [openQuestion, questionBudget])

  /** Back one step, to what this round is testing, without ending the sitting. */
  const backToModes = useCallback(() => {
    setStage('mode')
    setPhase('commit')
    setCurrentLog(null)
    setStartedAt(null)
    setElapsedSeconds(null)
    setLast(null)
  }, [])

  /** Back to the section picker without ending the sitting. */
  const restart = useCallback(() => {
    setStage('section')
    setPhase('commit')
    setSection(null)
    setFocusKey(null)
    setPaceLevelKey(null)
    setCurrentLog(null)
    setStartedAt(null)
    setElapsedSeconds(null)
    setLast(null)
  }, [])

  const answered = logs.length
  const correctCount = logs.filter(l => l.actualCorrect === true).length
  const testLogs = logs.filter(l => l.mode === MODE_TEST)

  // Consecutive questions where he nailed whatever that question asked him to call.
  const streak = (() => {
    let count = 0
    for (let i = logs.length - 1; i >= 0; i--) {
      if (focusHit(logs[i])) count++
      else break
    }
    return count
  })()

  return {
    ...session,
    catalog,
    pace,
    stage,
    mode,
    section,
    sectionSpec,
    sectionPace,
    focus,
    focusKey,
    paceLevel,
    paceLevelKey,
    setPaceLevelKey,
    questionBudget,
    runBudgetSeconds,
    questionsPlanned,
    recommendedPaceLevel,
    phase,
    currentLog,
    startedAt,
    elapsedSeconds,
    runStartedAt,
    logs,
    last,
    answered,
    correctCount,
    testLogs,
    streak,
    summary,
    strategyStats,
    busy,
    error,
    begin,
    chooseSection,
    startPractice,
    startTest,
    startQuestion,
    nextTestQuestion,
    stopTimer,
    grade,
    finish,
    backToModes,
    restart,
    setStage,
  }
}
